import {
  CasePlan,
  CurrentCaseSubscription,
  SubscriptionUsageStats,
} from '@app/types/payment/subscription';
import ApiHandler from '../ApiHandler';
import { urls } from '../urls';

/**
 * API service for case subscription management
 * Works alongside existing payment system for backward compatibility
 */
export class CaseSubscriptionApi {
  /**
   * Get all available subscription plans
   */
  static async getAvailablePlans(): Promise<CasePlan[]> {
    // ApiHandler already unwraps response.data via interceptor
    const data = await ApiHandler.get<CasePlan[]>(
      urls.caseSubscription.availablePlans
    );
    return data as any;
  }

  /**
   * Check if the current user is eligible for a free trial
   */
  static async getTrialEligibility(): Promise<{ eligible: boolean }> {
    const data = await ApiHandler.get<{ eligible: boolean }>(
      urls.caseSubscription.trialEligibility
    );
    return data as any;
  }

  /**
   * Get current user's subscription
   */
  static async getCurrentSubscription(): Promise<CurrentCaseSubscription> {
    // ApiHandler already unwraps response.data via interceptor
    const data = await ApiHandler.get<CurrentCaseSubscription>(
      urls.caseSubscription.current
    );
    return data as any;
  }

  /**
   * Start free trial (one-time only)
   */
  static async startFreeTrial(): Promise<CurrentCaseSubscription> {
    // ApiHandler already unwraps response.data via interceptor
    const data = await ApiHandler.post<CurrentCaseSubscription>(
      urls.caseSubscription.startTrial
    );
    return data as any;
  }

  /**
   * Subscribe to a paid plan
   * @param planPriceId - ID of the CasePlanPrice to subscribe to
   * @param stripeSubscriptionId - Stripe subscription ID from Stripe checkout
   */
  static async subscribe(
    planPriceId: number,
    stripeSubscriptionId: string
  ): Promise<CurrentCaseSubscription> {
    // ApiHandler already unwraps response.data via interceptor
    const data = await ApiHandler.post<CurrentCaseSubscription>(
      urls.caseSubscription.subscribe,
      {
        plan_price_id: planPriceId,
        stripe_subscription_id: stripeSubscriptionId,
      }
    );
    return data as any;
  }

  /**
   * Cancel current subscription
   * User retains access until billing period end
   */
  static async cancelSubscription(): Promise<CurrentCaseSubscription> {
    // ApiHandler already unwraps response.data via interceptor
    const data = await ApiHandler.post<CurrentCaseSubscription>(
      urls.caseSubscription.cancel
    );
    return data as any;
  }

  /**
   * Reactivate current subscription (undo cancel_at_period_end)
   */
  static async reactivateSubscription(): Promise<CurrentCaseSubscription> {
    const data = await ApiHandler.post<CurrentCaseSubscription>(
      urls.caseSubscription.reactivate as any
    );
    return data as any;
  }

  /**
   * Get detailed usage statistics
   */
  static async getUsageStats(): Promise<SubscriptionUsageStats> {
    // ApiHandler already unwraps response.data via interceptor
    const data = await ApiHandler.get<SubscriptionUsageStats>(
      urls.caseSubscription.usage
    );
    return data as any;
  }

  /**
   * Create a Stripe subscription with trial for a default or specified plan during signup
   * If planPriceId is omitted, backend will pick the lowest-priced active monthly plan
   */
  static async createTrialSubscription(
    email: string,
    planPriceId?: number
  ): Promise<CurrentCaseSubscription> {
    const payload: any = { email };
    if (planPriceId) payload.plan_price_id = planPriceId;
    const data = await ApiHandler.post<CurrentCaseSubscription>(
      urls.caseSubscription.createTrialSubscription as any,
      payload
    );
    return data as any;
  }

  /**
   * Create a Stripe Checkout Session for plan upgrade/subscribe
   */
  static async createCheckoutSession(
    planPriceId: number,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ url: string; session_id: string }> {
    const data = await ApiHandler.post(
      urls.caseSubscription.createCheckoutSession,
      {
        plan_price_id: planPriceId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      }
    );
    return data as any;
  }

  /**
   * Attempt direct upgrade using customer's payment method.
   * If backend returns checkout_required, caller should fallback to Checkout Session.
   * @param planPriceId - ID of the CasePlanPrice to upgrade to
   * @param paymentMethodId - Optional specific payment method ID to use (uses default if not provided)
   */
  static async upgrade(
    planPriceId: number,
    paymentMethodId?: string
  ): Promise<CurrentCaseSubscription> {
    const payload: any = { plan_price_id: planPriceId };
    if (paymentMethodId) {
      payload.payment_method_id = paymentMethodId;
    }
    const data = await ApiHandler.post<CurrentCaseSubscription>(
      urls.caseSubscription.upgrade,
      payload
    );
    return data as any;
  }
}
